import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import useActivity from '@hooks/useActivity'

import { renderActionText } from '@functions/activity'

import RecipientDetails from './RecipientDetails'

const MODAL_KEY = 'ManageActivityModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageActivityModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { activity: initActivity } = modalPayload

  const activityPayload = useActivity(
    initActivity,
    {
      requestOptions: {
        include_activity_owner: true,
        include_activity_recipient: true,
      },
      performHttpRequests: true,
    },
  )

  const {
    activity,
    activity: {
      owner, parameters, recipient, trackable,
    },
    activityHasReason,
    formattedDateLong,
    hasChanges,
    isDestructive,
    recipientIsTrackable,
    urls: { exportActivity },
  } = activityPayload

  const { image, role, title } = owner || {}

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Activity" />

      <SidebarModal.Content>
        <Form.Field label="Action">
          <Text color={isDestructive ? 'red' : 'green'} fontSize="small">{renderActionText(activity)}</Text>
        </Form.Field>

        <Form.Field label={recipient?.type} marginTop="large">
          <Box alignItems="center" fontSize="small" justifyContent="space-between">
            <Text variant="twoLineEllipsis" width={280}>{recipient?.title || recipient?.type}&nbsp;</Text>

            <Text color="bodyFontLightColor" variant="ellipsis">#&nbsp;{recipient?.id}</Text>
          </Box>
        </Form.Field>

        {!recipientIsTrackable && (
          <Form.Field label={trackable.type} marginTop="large">
            <Box alignItems="center" fontSize="small" justifyContent="space-between">
              <Text variant="twoLineEllipsis" width={280}>{trackable.title || trackable.type}&nbsp;</Text>

              <Text color="bodyFontLightColor" variant="ellipsis">#&nbsp;{trackable.id}</Text>
            </Box>
          </Form.Field>
        )}

        {activityHasReason && (
          <Form.Field label="Reason" marginTop="large">
            <Text variant="twoLineEllipsis" width={280}>{trackable.reason || 'N/A'}</Text>
          </Form.Field>
        )}

        <Form.Field label="Date" marginTop="large">
          <Text fontSize="small">{formattedDateLong}</Text>
        </Form.Field>

        <Form.Field label="User" marginTop="large">
          <SidebarModal.User
            boxProps={{ paddingTop: 'none' }}
            disableHover
            imageUrl={image?.square_url}
            subtitle={role}
            title={title}
          />
        </Form.Field>

        {['Email', 'SMS'].includes(parameters?.type || '') && <RecipientDetails parameters={parameters} />}

        <Form.Field label="More Options" marginTop={hasChanges ? 'large' : 0}>
          <AuthenticatedDownload>
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faDownload} />}
              href={exportActivity}
              size="medium"
            >
              Download
            </Button>
          </AuthenticatedDownload>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  // @ts-expect-error TS2322
  <SidebarModal.RenderController {...props}>
    <ManageActivityModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
